import type { AppRole, Member, Ministry, Session } from './types';

/**
 * O que cada perfil pode fazer — espelho, na interface, das políticas de acesso por
 * linha do servidor (`supabase/schema.sql` do handoff). A interface esconde o que não é
 * permitido; quem garante de verdade é o servidor.
 *
 * Dois níveis de liderança: o Líder de Ministério cuida do ministério inteiro; o Líder de
 * Função, só das funções que lidera (escala, regras, sexo permitido e habilitados). A
 * liderança é o que vale: ser nomeado líder (`leaderIds`) já dá o poder, e deixar de ser
 * o tira — sem trocar perfil em Acessos. Uma pessoa pode liderar um ministério e funções
 * de outros ao mesmo tempo, e cada ministério ou função pode ter mais de um líder.
 */

/** Quem cria e gerencia as organizações (fora do perfil de cada organização). */
export const PLATFORM_ADMIN_LABEL = 'Administrador da plataforma';

export const ROLE_LABEL: Record<AppRole, string> = {
  admin: 'Administrador da organização',
  leader: 'Líder de Ministério',
  roleLeader: 'Líder de Função',
  member: 'Membro',
};

/** Recorte do que a equipe vê (dashboard, avisos, distribuição); `null` = tudo. */
export interface Scope {
  ministryIds: readonly string[];
  /** `null` = todas as funções dos ministérios do recorte. */
  roleIds: readonly string[] | null;
}

export interface Permissions {
  role: AppRole;
  memberId: string;
  /** Ministérios que o usuário lidera como Líder de Ministério. */
  ledMinistryIds: string[];
  /** Funções que o usuário lidera como Líder de Função. */
  ledRoleIds: string[];
  /** Administrador ou um dos líderes: vê status, conflitos e edita escalas. */
  isManager: boolean;
  isAdmin: boolean;
  isRoleLeader: boolean;
  scope: Scope | null;
  manageMinistries: boolean;
  manageEvents: boolean;
  manageMembers: boolean;
  manageSettings: boolean;
  /** Convida membros, troca o perfil de acesso e remove acessos (tela Acessos). */
  manageAccess: boolean;
  generate: boolean;
  linkSpouse: boolean;
  /** Cria, edita, suspende e troca de organização (Administrador da plataforma). */
  manageOrgs: boolean;
  /** Muda o endereço de acesso (slug) da organização — só a plataforma. */
  editOrgSlug: boolean;
  /** Vê e aprova os cadastros que chegam pelo link público. */
  reviewRegistrations: boolean;
  /** Vê e responde aos pedidos de privacidade dos membros (LGPD). */
  answerPrivacy: boolean;
  /** Abre a Membresia (indicadores, público dos cultos): o Administrador e os líderes. */
  viewMembership: boolean;
  /** Registra e edita o público do culto: o Administrador. */
  recordAudience: boolean;
  /**
   * Distribuição geográfica da Membresia: conta por bairro a partir do endereço, que só o
   * Administrador vê (`viewPrivateData`).
   */
  viewGeography: boolean;
  /**
   * Gerencia as fichas de visitantes (contato, acompanhamentos, encaminhamento): o
   * Administrador e quem lidera o ministério de acolhimento (`Ministry.welcome`) ou uma
   * função dele. Os outros líderes veem só os números (`visitorSummaryOf`).
   */
  manageVisitors: boolean;
  /** Edita o ministério inteiro: funções, quantidades e Líderes de Função. */
  editMinistry: (ministryId: string) => boolean;
  /** Edita a escala, as regras, o sexo permitido e os habilitados de uma função. */
  editRole: (roleId: string) => boolean;
  /** Edita a escala de ao menos uma função do ministério. */
  scheduleMinistry: (ministryId: string) => boolean;
  editMemberInfo: (memberId: string) => boolean;
  /** Vê endereço e contato de emergência: o Administrador e o próprio membro. */
  viewPrivateData: (memberId: string) => boolean;
  editAvailability: (member: Member) => boolean;
}

/** Ministérios que a pessoa lidera e funções que lidera fora deles. */
export function leadershipOf(memberId: string, ministries: readonly Ministry[]) {
  const ministryIds = ministries.filter((m) => m.leaderIds.includes(memberId)).map((m) => m.id);
  const roles = ministries
    .filter((m) => !ministryIds.includes(m.id))
    .flatMap((m) => m.roles.filter((r) => r.leaderIds.includes(memberId)).map((r) => ({ m, r })));

  return { ministryIds, roles };
}

/** O que a pessoa lidera, para ler: "Recepção, Projeção (Multimídia)"; `''` sem liderança. */
export function leadershipLabel(memberId: string, ministries: readonly Ministry[]): string {
  const led = leadershipOf(memberId, ministries);

  return [
    ...ministries.filter((m) => led.ministryIds.includes(m.id)).map((m) => m.name),
    ...led.roles.map(({ m, r }) => `${r.name} (${m.name})`),
  ].join(', ');
}

/** "Líder: Paula", "Líderes: Paula e Ana", "Líderes: Paula, Ana e Rui" ou "Sem líder". */
export function leadersText(names: readonly string[]): string {
  if (!names.length) return 'Sem líder';
  if (names.length === 1) return `Líder: ${names[0]}`;

  return `Líderes: ${names.slice(0, -1).join(', ')} e ${names.at(-1)}`;
}

/**
 * O perfil de acesso da pessoa: Administrador quando escolhido em Acessos (`admin`); senão
 * Líder de Ministério se lidera algum ministério, Líder de Função se lidera só funções e
 * Membro se não lidera nada.
 */
export function profileOf(memberId: string, admin: boolean, ministries: readonly Ministry[]): AppRole {
  if (admin) return 'admin';

  const led = leadershipOf(memberId, ministries);

  if (led.ministryIds.length) return 'leader';

  return led.roles.length ? 'roleLeader' : 'member';
}

export function permissionsOf(session: Session, ministries: readonly Ministry[]): Permissions {
  const { memberId } = session;
  const platformAdmin = session.platformAdmin === true;
  // O Administrador da plataforma é Administrador em qualquer organização em que entrar.
  const isAdmin = session.role === 'admin' || platformAdmin;
  const role = profileOf(memberId, isAdmin, ministries);
  const isRoleLeader = role === 'roleLeader';
  const ministryOf = new Map(ministries.flatMap((m) => m.roles.map((r) => [r.id, m.id] as const)));
  const led = isAdmin ? { ministryIds: [], roles: [] } : leadershipOf(memberId, ministries);
  const ledMinistryIds = led.ministryIds;
  const ledRoles = led.roles;
  const ledRoleIds = ledRoles.map((x) => x.r.id);
  const editMinistry = (ministryId: string) => isAdmin || ledMinistryIds.includes(ministryId);
  const editRole = (roleId: string) => {
    const ministryId = ministryOf.get(roleId);

    return !!ministryId && (editMinistry(ministryId) || ledRoleIds.includes(roleId));
  };

  const scopeOf = (): Scope | null => {
    if (isAdmin) return null;
    if (!ledRoles.length) return { ministryIds: ledMinistryIds, roleIds: null };

    // Funções soltas: o recorte vira por função, com todas as dos ministérios que lidera.
    const whole = ministries.filter((m) => ledMinistryIds.includes(m.id));

    return {
      ministryIds: [...ledMinistryIds, ...new Set(ledRoles.map((x) => x.m.id))],
      roleIds: [...whole.flatMap((m) => m.roles.map((r) => r.id)), ...ledRoleIds],
    };
  };

  return {
    role,
    memberId,
    ledMinistryIds,
    ledRoleIds,
    isManager: role !== 'member',
    isAdmin,
    isRoleLeader,
    scope: scopeOf(),
    manageMinistries: isAdmin,
    manageEvents: isAdmin,
    manageMembers: isAdmin,
    manageSettings: isAdmin,
    manageAccess: isAdmin,
    generate: role !== 'member',
    linkSpouse: isAdmin,
    manageOrgs: platformAdmin,
    editOrgSlug: platformAdmin,
    reviewRegistrations: isAdmin,
    answerPrivacy: isAdmin,
    viewMembership: role !== 'member',
    recordAudience: isAdmin,
    viewGeography: isAdmin,
    manageVisitors:
      isAdmin ||
      ministries.some(
        (m) => m.welcome && (ledMinistryIds.includes(m.id) || ledRoles.some((x) => x.m.id === m.id))
      ),
    editMinistry,
    editRole,
    scheduleMinistry: (ministryId) => editMinistry(ministryId) || ledRoles.some((x) => x.m.id === ministryId),
    editMemberInfo: (id) => isAdmin || id === memberId,
    viewPrivateData: (id) => isAdmin || id === memberId,
    editAvailability: (member) =>
      isAdmin ||
      member.id === memberId ||
      ledMinistryIds.some((id) => (member.roles[id] ?? []).length > 0) ||
      ledRoles.some((x) => (member.roles[x.m.id] ?? []).includes(x.r.id)),
  };
}

/** O membro está habilitado em algo do recorte (sem recorte: em qualquer função). */
export function memberInScope(member: Member, scope: Scope | null): boolean {
  if (!scope) return Object.keys(member.roles).length > 0;

  const { roleIds } = scope;

  if (roleIds) return Object.values(member.roles).some((ids) => ids.some((id) => roleIds.includes(id)));

  return scope.ministryIds.some((id) => (member.roles[id] ?? []).length > 0);
}

/** Nome do recorte: "Recepção" (ministérios) ou "Projeção (Multimídia)" (funções). */
export function scopeLabel(scope: Scope | null, ministries: readonly Ministry[]): string {
  if (!scope) return '';

  const names = scope.roleIds
    ? ministries.flatMap((m) =>
        m.roles.filter((r) => scope.roleIds!.includes(r.id)).map((r) => `${r.name} (${m.name})`)
      )
    : ministries.filter((m) => scope.ministryIds.includes(m.id)).map((m) => m.name);

  return names.join(', ');
}

/** Matriz exibida em Configurações › Perfis e permissões. */
export const PERMISSION_MATRIX: readonly ({ label: string } & Record<AppRole, string>)[] = [
  {
    label: 'Ver dashboard',
    admin: 'Geral',
    leader: 'Seus ministérios',
    roleLeader: 'Suas funções',
    member: 'Agenda própria',
  },
  { label: 'Criar e editar ministérios', admin: 'Sim', leader: 'Não', roleLeader: 'Não', member: 'Não' },
  {
    label: 'Editar funções',
    admin: 'Sim',
    leader: 'Seu ministério',
    roleLeader: 'Regras e habilitados das suas',
    member: 'Não',
  },
  {
    label: 'Definir Líder de Função',
    admin: 'Sim',
    leader: 'Seu ministério',
    roleLeader: 'Não',
    member: 'Não',
  },
  { label: 'Criar e editar eventos', admin: 'Sim', leader: 'Não', roleLeader: 'Não', member: 'Não' },
  {
    label: 'Gerar, editar e publicar escala',
    admin: 'Sim',
    leader: 'Seu ministério',
    roleLeader: 'Suas funções',
    member: 'Não',
  },
  {
    label: 'Check-in e cobrança de pendentes',
    admin: 'Sim',
    leader: 'Seu ministério',
    roleLeader: 'Suas funções',
    member: 'Não',
  },
  {
    label: 'Confirmar presença ou "Não posso ir"',
    admin: 'A própria',
    leader: 'A própria',
    roleLeader: 'A própria',
    member: 'A própria',
  },
  { label: 'Cadastrar e importar membros', admin: 'Sim', leader: 'Não', roleLeader: 'Não', member: 'Não' },
  {
    label: 'Link de cadastro e aprovação',
    admin: 'Sim',
    leader: 'Não',
    roleLeader: 'Não',
    member: 'Não',
  },
  {
    label: 'Endereço e contato de emergência',
    admin: 'Ver e editar',
    leader: 'Não vê',
    roleLeader: 'Não vê',
    member: 'Os próprios',
  },
  {
    label: 'Disponibilidade e ausências',
    admin: 'Sim',
    leader: 'Membros do ministério',
    roleLeader: 'Membros das suas funções',
    member: 'A própria',
  },
  { label: 'Cônjuge', admin: 'Vincular', leader: 'Ver', roleLeader: 'Ver', member: 'Informar nome' },
  { label: 'Convidar e gerenciar acessos', admin: 'Sim', leader: 'Não', roleLeader: 'Não', member: 'Não' },
  {
    label: 'Fichas e acompanhamento de visitantes',
    admin: 'Sim',
    leader: 'Se lidera o acolhimento',
    roleLeader: 'Se lidera o acolhimento',
    member: 'Não',
  },
  {
    label: 'Privacidade: cópia, correção e pedidos (LGPD)',
    admin: 'Os próprios',
    leader: 'Os próprios',
    roleLeader: 'Os próprios',
    member: 'Os próprios',
  },
  {
    label: 'Responder aos pedidos de privacidade',
    admin: 'Sim',
    leader: 'Não',
    roleLeader: 'Não',
    member: 'Não',
  },
  { label: 'Configurações', admin: 'Sim', leader: 'Não', roleLeader: 'Não', member: 'Não' },
];
